/**
 * AudienceUserJoinedVideoCallUseCase
 *
 * Records that the audience user joined the channel by writing their Agora
 * streaming id onto the `videoCall` message and (re-)asserting `isLive = true`.
 * The caller (audience role) fires this from the Agora `join`/`user-joined`
 * path; the resulting `modified` change is what the caller's live subscription
 * keys on to learn the audience is present (`video_call_screen.dart:364-365`).
 *
 * Flutter source: lib/presentation/screens/chat/chat_screen.dart:842-863
 *   (`audienceUserJoinedVideoCall`):
 *     - `firebaseUser == null` -> return (`:846-849`).
 *     - locate the message by `videoCallId == messageId` (`:851-853`); on web the
 *       caller supplies the already-resolved entity.
 *     - if found, set `isLive = true`, `audienceStreamingId = audienceUserStreamingId`,
 *       then persist via `ChatRepository.saveChatMessage(_e2ee, …)` (`:854-861`).
 *
 * Note there is NO `isLive`-state guard here (unlike `endVideoCallMessage`) —
 * Flutter writes whenever the message exists, so the web port does too.
 *
 * Empty content -> empty-string `''` on the save path (NOT `null`). Flutter
 * writes `content: ''` / `last_content: ''` (chat_repository.dart:20-25
 * else-branch + chat_data_source.dart:35); a `null` `last_content` crashes
 * Flutter's non-nullable parse (chat_message_node.dart:42). The service maps
 * `''` to `last_content: ''`.
 *
 * Pure-domain contract: mutates + persists the supplied entity; no Redux / UI.
 */

import { ChatService } from '@/domain/services/chat/chatService';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import { auth } from '@/data/services/firebase';

export interface AudienceUserJoinedVideoCallParams {
  /** The call session id (`videoCallId`). Carried for parity / logging. */
  callMessageId: string;
  /** The audience user's local Agora streaming id (`audienceStreamingId`). */
  audienceStreamingId: number;
  /**
   * The current `videoCall` message entity (the web analog of Flutter's
   * `_listItems.firstWhereOrNull`). `null` -> no-op.
   */
  message: ChatMessageEntity | null;
  /** Chat-users context forwarded to the save path for the metadata doc. */
  chatUsersInfo: ChatUsersInfoEntity;
}

export class AudienceUserJoinedVideoCallUseCase {
  private chatService: ChatService;

  constructor(chatService: ChatService = ChatService.getInstance()) {
    this.chatService = chatService;
  }

  /**
   * Mark the audience as joined on the call message.
   *
   * @returns the `ChatService.saveChatMessage` boolean when the write runs;
   *   `false` when there is no authenticated user (`:846-849`) or no message.
   */
  async execute({
    audienceStreamingId,
    message,
    chatUsersInfo,
  }: AudienceUserJoinedVideoCallParams): Promise<boolean> {
    // Parity `chat_screen.dart:846-849` — no authenticated user -> return.
    if (auth.currentUser == null) {
      return false;
    }

    // Parity `chat_screen.dart:854-861` — write only when the message exists.
    if (message != null) {
      message.isLive = true;
      message.audienceStreamingId = audienceStreamingId;
      // Empty content -> empty-string `''` (NOT `null`); the service maps it to
      // `last_content: ''` / `content: ''` (parity chat_data_source.dart:35).
      return this.chatService.saveChatMessage(message, chatUsersInfo, '');
    }

    return false;
  }
}

// Export singleton instance
export const audienceUserJoinedVideoCallUseCase = new AudienceUserJoinedVideoCallUseCase();
